import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock, Calendar, UserCheck, Split } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { calculateWillingnessGates } from '@/lib/willingnessGates';
import { calculateTPS } from '@/utils/proficiencyMatcher';
import { cultureNumber } from '@/lib/shopCulture/shopCultureDefaults';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';

interface DueOutMitigationModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: any;
  currentTechName: string;
  dueOutTime: Date;
  predictedFinish: Date;
  technicians?: Array<{ id: string; name: string; is_present: boolean }>;
  onActionComplete?: () => void;
}

export const DueOutMitigationModal = ({
  isOpen,
  onClose,
  ticket,
  currentTechName,
  dueOutTime,
  predictedFinish,
  technicians = [],
  onActionComplete,
}: DueOutMitigationModalProps) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const delayMinutes = Math.round((predictedFinish.getTime() - dueOutTime.getTime()) / 60000);

  const handleStartToday = async () => {
    setIsProcessing(true);
    setActionInProgress('start-today');

    try {
      // Check if there's a scheduled_start_time that's preventing immediate start
      if (ticket.scheduled_start_time) {
        // Chronomancer R-WAITER-1: canonical Waiter→Drop-off conversion.
        // Previously wrote `ticket_type: 'Due-In'` — column does not exist
        // on tickets (schema has `type` with enum Waiter | Drop-off, and
        // `original_ticket_type` for history). PostgREST would reject with
        // PGRST204 "column not found", the catch block would surface
        // "Failed to update" to the advisor, and the Waiter's promise
        // would silently remain un-mitigated. The correct operation when
        // clearing scheduled_start_time on a Waiter is to drop the
        // appointment semantics entirely — i.e. convert to Drop-off.
        //
        // V-A-8: routed through mutateTicketSchedule. scheduled_start_time
        // is the schedule field; type is companion metadata that must land
        // atomically with the schedule write. skipCascade:true preserves
        // the bespoke triggerBoardCascade below (scoped by tech + scheduled_date).
        const result = await mutateTicketSchedule(
          ticket.id,
          { scheduled_start_time: null },
          {
            reason: 'due_out_mitigation_start_today',
            skipCascade: true,
            companionFields: { type: 'Drop-off' },
          }
        );
        if (!result.success) throw new Error(result.error || 'Failed to update ticket');

        // R49 FIX-2: Log Pensieve event for audit trail
        await supabase
          .from('pensieve_events')
          .insert({
            shop_id: ticket.shop_id,
            ticket_id: ticket.id,
            session_id: `due-out-mitigation-${Date.now()}`,
            event_type: 'due-out-mitigation',
            metadata: { cleared_scheduled_start_time: true },
          })
          .then(({ error: pErr }) => {
            if (pErr) console.warn('[DueOutMitigation] Pensieve log error:', pErr);
          });

        // Codex: Trigger cascade — ticket changed from time-anchored to flexible
        const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
        await triggerBoardCascade(
          ticket.shop_id,
          ticket.scheduled_date,
          ticket.assigned_tech_id
        ).catch(err => console.warn('[DueOutMitigation] Cascade error:', err));

        toast({
          title: 'Ticket Updated',
          description: 'Ticket moved to ASAP start to meet due out time.',
        });
      } else {
        toast({
          title: 'Already Set for ASAP',
          description: 'This ticket is already scheduled to start as soon as possible.',
          variant: 'default',
        });
      }

      onActionComplete?.();
      onClose();
    } catch (error) {
      console.error('Error starting today:', error);
      toast({
        title: 'Error',
        description: 'Failed to update ticket start time.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
      setActionInProgress(null);
    }
  };

  const handleSplitTicket = async () => {
    setIsProcessing(true);
    setActionInProgress('split');

    try {
      // Check if ticket can be split
      if (!ticket.jobs || ticket.jobs.length < 2) {
        toast({
          title: 'Cannot Split',
          description: 'Ticket must have at least 2 jobs to split.',
          variant: 'destructive',
        });
        setIsProcessing(false);
        setActionInProgress(null);
        return;
      }

      // Navigate to split preview (OptiPlace modal with split analysis)
      window.location.href = `/create-ticket?split=${ticket.id}`;
      onClose();
    } catch (error) {
      console.error('Error navigating to split:', error);
      toast({
        title: 'Error',
        description: 'Failed to open split preview.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
      setActionInProgress(null);
    }
  };

  const handleReassignTech = async () => {
    setIsProcessing(true);
    setActionInProgress('reassign');

    try {
      // Get shop culture for willingness gates
      // shop_culture is a jsonb COLUMN of shop_profile (not a relation), and shop_profile is keyed by
      // shop_id (its own id is a separate primary key). The previous query embedded `shop_culture(...)`
      // and filtered `id = shop_id`, so it could never succeed and Reassign always failed.
      const { data: shopData, error: shopError } = await supabase
        .from('shop_profile')
        .select('shop_culture')
        .eq('shop_id', ticket.shop_id)
        .maybeSingle();

      if (shopError) throw shopError;

      // `??`, not `||`: a shop that chose willingness 0 must not be treated as 50.
      const willingness = cultureNumber(shopData?.shop_culture, 'willingness_to_reassign');
      const gates = calculateWillingnessGates(willingness);

      console.log('[DueOutMitigation] Willingness gates:', gates);

      // Core-100 R4 X4 (CORE-AD S, GAP-013): the SERVER picks. The browser used to rank every technician itself
      // (the browser-side proficiency ranker) and tell reassign-ticket "OptiBay chose this one" — a claim the server could not
      // check. Now smart-assign ranks with the server's own rules (work family, absences, capacity, lift safety),
      // skipping the technician the ticket is already with, and RECORDS its pick (dispatch_server_picks);
      // reassign-ticket accepts 'due-out-mitigation' only against that record (automatic + server). The browser can
      // only accept the pick or decline it — the shop's willingness gate below is a veto, never a different choice.
      const ticketJobs = ((ticket.jobs ?? []) as Array<Record<string, any>>).map((job) => ({
        id: job.id,
        name: job.name,
        category: job.category,
        proficiency_category: job.proficiency_category || job.category,
        skill: job.skill_level || job.skillLevel || 3,
        minutes: job.standard_minutes || job.standardMinutes || 30,
        bay: job.bay_requirement || job.bayRequirement || 'Flat',
      }));
      const { data: ticketData } = await supabase
        .from('tickets')
        .select('scheduled_date')
        .eq('id', ticket.id)
        .single();
      const { data: pickResult, error: pickError } = await supabase.functions.invoke('smart-assign', {
        body: {
          ticketData: {
            shop_id: ticket.shop_id,
            type: ticket.type || 'Drop-off',
            jobs: ticketJobs,
            // smart-assign plans from predictedMinutes (without it every technician's slot search is NaN).
            predictedMinutes: ticket.predicted_minutes ?? ticket.predictedMinutes
              ?? ticketJobs.reduce((sum, j) => sum + (Number(j.minutes) || 0), 0),
            appointmentDate: ticketData?.scheduled_date ?? ticket.scheduled_date,
            vehicleInfo: ticket.vehicle_info || ticket.vehicleInfo,
          },
          skipSplitCheck: true,
          pickForTicketId: ticket.id,
          pickExcludeTechId: ticket.assigned_tech_id ?? undefined,
        },
      });
      if (pickError) throw pickError;
      const serverPickId: string | null = pickResult?.data?.serverPickId ?? null;
      const rankings = (pickResult?.data?.rankings ?? pickResult?.rankings ?? []) as Array<{
        technicianId: string; technicianName?: string; suggestedStartTime?: string | null;
      }>;
      const pick = rankings.find((r) => r.technicianId !== ticket.assigned_tech_id) ?? null;

      // The shop's willingness to move work (Shop Profile) may still DECLINE the server's pick when its proficiency
      // gap to the current technician is too large. It cannot substitute another technician.
      let declinedByWillingness = false;
      if (pick && serverPickId) {
        const { data: twoTechs } = await supabase
          .from('technicians')
          .select('id, proficiencies')
          .in('id', [pick.technicianId, ticket.assigned_tech_id].filter(Boolean) as string[]);
        const profOf = (id: string | null | undefined) =>
          ((twoTechs ?? []).find((t) => t.id === id)?.proficiencies ?? {}) as Record<string, number>;
        const currentTPS = ticket.assigned_tech_id ? calculateTPS(ticket.jobs, profOf(ticket.assigned_tech_id)) : 0;
        const pickTPS = calculateTPS(ticket.jobs, profOf(pick.technicianId));
        declinedByWillingness = Math.abs(pickTPS - currentTPS) > gates.profGapMax;
        console.log(`[DueOutMitigation] server pick ${pick.technicianName ?? pick.technicianId}, TPS gap ${Math.abs(pickTPS - currentTPS).toFixed(1)}%`);
      }

      if (!pick || !serverPickId || declinedByWillingness) {
        toast({
          title: 'No Alternative Found',
          description: declinedByWillingness
            ? `OptiBay's best alternative is outside the shop's willingness to reassign (${willingness}%). Consider increasing willingness in Shop Profile.`
            : 'OptiBay found no other technician who can take this ticket.',
          variant: 'default',
        });
        setIsProcessing(false);
        setActionInProgress(null);
        return;
      }

      const { error: reassignError } = await supabase.functions.invoke('reassign-ticket', {
        body: {
          ticketId: ticket.id,
          targetTechId: pick.technicianId,
          scheduledDate: ticketData?.scheduled_date,
          desiredStartTime: pick.suggestedStartTime ?? undefined,
          moveReason: 'due-out-mitigation',
          serverPickId,
        },
      });
      if (reassignError) throw reassignError;
      const bestAlternative = { tech: { name: pick.technicianName ?? 'the selected technician', id: pick.technicianId } };

      toast({
        title: 'Ticket Reassigned',
        description: `Reassigned to ${bestAlternative.tech.name} (OptiBay's pick, recorded by the server)`,
      });

      onActionComplete?.();
      onClose();
    } catch (error) {
      console.error('Error reassigning tech:', error);
      toast({
        title: 'Error',
        description: 'Failed to reassign technician.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
      setActionInProgress(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Due Out Time Conflict
          </DialogTitle>
          <DialogDescription>
            This ticket is predicted to miss its promised due out time by{' '}
            <span className="font-semibold text-destructive">{delayMinutes} minutes</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Conflict Summary */}
          <Alert variant="destructive">
            <Clock className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Promised Due Out:</span>
                  <span className="font-medium">{format(dueOutTime, 'h:mm a')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Predicted Finish:</span>
                  <span className="font-medium">{format(predictedFinish, 'h:mm a')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Tech:</span>
                  <span className="font-medium">{currentTechName}</span>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* Mitigation Actions */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Choose a mitigation action:</p>

            {/* Start Today ASAP */}
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={handleStartToday}
              disabled={isProcessing}
            >
              <div className="flex items-start gap-3 text-left">
                <Calendar className="h-5 w-5 mt-0.5 text-primary" />
                <div className="flex-1">
                  <div className="font-semibold">Start ASAP Today</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Remove scheduled start time and prioritize this ticket to start immediately
                  </div>
                </div>
              </div>
              {actionInProgress === 'start-today' && (
                <span className="ml-auto text-xs">Processing...</span>
              )}
            </Button>

            {/* Split Ticket */}
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={handleSplitTicket}
              disabled={isProcessing || !ticket.jobs || ticket.jobs.length < 2}
            >
              <div className="flex items-start gap-3 text-left">
                <Split className="h-5 w-5 mt-0.5 text-primary" />
                <div className="flex-1">
                  <div className="font-semibold">Split Ticket</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Divide jobs between multiple techs based on skill gaps to reduce makespan
                  </div>
                  {(!ticket.jobs || ticket.jobs.length < 2) && (
                    <div className="text-xs text-destructive mt-1">
                      Requires at least 2 jobs
                    </div>
                  )}
                </div>
              </div>
              {actionInProgress === 'split' && (
                <span className="ml-auto text-xs">Processing...</span>
              )}
            </Button>

            {/* Reassign Tech */}
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={handleReassignTech}
              disabled={isProcessing}
            >
              <div className="flex items-start gap-3 text-left">
                <UserCheck className="h-5 w-5 mt-0.5 text-primary" />
                <div className="flex-1">
                  <div className="font-semibold">Reassign Technician</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Find best alternative tech based on proficiency and availability (respects Willingness gates)
                  </div>
                </div>
              </div>
              {actionInProgress === 'reassign' && (
                <span className="ml-auto text-xs">Processing...</span>
              )}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
